import React, { useContext, useEffect, useState } from "react";
import CustomTable from "../../../shared/custom-table/custom-table";
import { columnsProf } from "../../../mocks/fakeData";
import {
  addTeacherToSuperTeacherService,
  getAllTeacherFromSuperTeacher,
  getAllUserByRole,
  removeTeacherToSuperTeacherService,
} from "../../../services/super-teacher";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  IconButton,
  Modal,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { SnackbarContext } from "../../../config/hooks/use-toast";
import CustomAutocomplete from "../../../shared/custom-autoComplete/custom-autocomplete";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faUserTie, 
  faPlus, 
  faTrash, 
  faCheck,
  faBuildingUser,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";

const ManagementProf = () => {
  const [data, setData] = useState<any>([]);
  const [teachers, setTeachers] = useState<any>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<string | number>("");
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const snackbarContext = useContext(SnackbarContext);

  const fetchData = () => {
    getAllTeacherFromSuperTeacher()
      .then((res) => {
        setData(res.data);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  useEffect(() => {
    fetchData();
    getAllUserByRole("ROLE_TEACHER")
      .then((res) => {
        const teacherOptions = res.data.map((teacher: any) => ({
          label: teacher.fullName,
          value: teacher.id,
        }));
        setTeachers(teacherOptions);
      })
      .catch((e) => {
        console.log(e);
      });
  }, []);

  const handleAddClick = () => setOpenAddDialog(true);
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSelectedTeacher("");
  };

  const handleSave = () => {
    if (!selectedTeacher) {
      snackbarContext?.showMessage("Erreur", "Veuillez choisir un enseignant", "error");
      return;
    }
    addTeacherToSuperTeacherService(selectedTeacher)
      .then(() => {
        fetchData();
        snackbarContext?.showMessage("Succès", "Professeur ajouté avec succès", "success");
        handleCloseAddDialog();
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const handleDeleteClick = (row: any) => {
    setSelectedRow(row);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedRow(null);
  };

  const handleDeleteConfirm = () => {
    if (selectedRow) {
      removeTeacherToSuperTeacherService(selectedRow.id)
        .then(() => {
          fetchData();
          snackbarContext?.showMessage("Succès", "Professeur retiré avec succès", "success");
        })
        .catch((e) => {
            console.log(e);
            snackbarContext?.showMessage("Erreur", "Impossible de supprimer cet enseignant", "error");
        });
    }
    handleCloseDeleteDialog();
  };

  const filteredData = data.filter((item: any) => 
    item.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.phoneNumber?.includes(searchQuery)
  );

  const ActionButtons: React.FC<{ row: any }> = ({ row }) => (
    <div className="flex items-center justify-center">
      <button
        className="w-10 h-10 flex items-center justify-center rounded-xl bg-rose-50 text-rose-500 hover:bg-rose-500 hover:text-white transition-all duration-300 active:scale-95 group shadow-sm hover:shadow-rose-200"
        onClick={() => handleDeleteClick(row)}
        title="Supprimer"
      >
        <FontAwesomeIcon icon={faTrash} className="text-sm group-hover:scale-110 transition-transform" />
      </button>
    </div>
  );

  const renderActions = (row: any) => <ActionButtons row={row} />;

  return (
    <div className="min-h-screen bg-[#F8FAFB] p-6 lg:p-12 relative overflow-hidden">
      {/* Background Decorative Elem */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] -mr-64 -mt-64 pointer-events-none"></div>
      
      <div className="max-w-[1600px] mx-auto space-y-12 relative z-10">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 pb-2">
            <div>
               <div className="flex items-center gap-4 mb-2">
                  <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center text-primary">
                     <FontAwesomeIcon icon={faBuildingUser} className="text-xl" />
                  </div>
                  <h1 className="text-4xl font-black text-title uppercase tracking-tighter">Gestion Professeurs</h1>
               </div>
               <p className="text-[11px] font-bold text-primary uppercase tracking-[0.4em] ml-1">Administration de votre corps enseignant</p>
            </div>
            
            <button 
                onClick={handleAddClick}
                className="h-16 px-10 bg-[#05322B] text-white rounded-[1.5rem] font-black text-[11px] uppercase tracking-[0.3em] shadow-2xl shadow-black/20 hover:bg-primary hover:-translate-y-1 active:scale-95 transition-all duration-500 flex items-center gap-4 group"
            >
               <span>Ajouter un Prof</span>
               <div className="w-6 h-6 rounded-lg bg-white/20 flex items-center justify-center group-hover:rotate-90 transition-transform duration-500">
                  <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
               </div>
            </button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
           <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-black/5 border border-white flex items-center gap-6">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center text-primary shadow-inner">
                 <FontAwesomeIcon icon={faUserTie} className="text-2xl" />
              </div>
              <div className="flex flex-col">
                 <span className="text-3xl font-black text-title tracking-tight">{data.length}</span>
                 <span className="text-[10px] font-black text-text/30 uppercase tracking-widest mt-1">Total Enseignants</span>
              </div>
           </div>
           
           <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-black/5 border border-white flex items-center gap-6 md:col-span-2">
              <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-text/20">
                 <SearchIcon />
              </div>
              <div className="flex-1">
                 <input 
                    type="text" 
                    placeholder="RECHERCHER UN NOM, EMAIL OU NUMÉRO..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full h-12 bg-transparent border-none outline-none font-black text-title text-sm placeholder:text-text/20 uppercase tracking-widest"
                 />
              </div>
           </div>
        </div>

        {/* Table Section */}
        <div className="relative group">
           <div className="absolute inset-0 bg-primary/20 blur-[100px] opacity-0 group-hover:opacity-20 transition-opacity duration-1000 -z-10"></div>
           <CustomTable
             title="Liste des Enseignants"
             columns={columnsProf}
             data={filteredData}
             actions={renderActions}
           />
        </div>
      </div>

      {/* Add Teacher Drawer (Side Modal) */}
      <Modal
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        className="flex justify-end backdrop-blur-sm bg-title/20"
        closeAfterTransition
      >
        <div className={`h-full w-full max-w-[550px] bg-white shadow-[-20px_0_80px_rgba(0,0,0,0.1)] outline-none transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col ${openAddDialog ? 'translate-x-0' : 'translate-x-full'}`}>
          
          {/* Drawer Header */}
          <div className="p-8 pb-6 flex items-center justify-between border-b border-gray-50 bg-white/50 backdrop-blur-md flex-shrink-0 z-30">
            <div>
              <h2 className="text-2xl font-black text-title uppercase tracking-tighter">Ajouter Prof</h2>
              <p className="text-[9px] font-black text-primary uppercase tracking-[0.4em] mt-1">Nouveau membre de l'équipe</p>
            </div>
            <button 
              onClick={handleCloseAddDialog}
              className="w-12 h-12 flex items-center justify-center rounded-2xl bg-gray-50 text-text/30 hover:bg-rose-50 hover:text-rose-500 transition-all duration-300 group"
            >
               <span className="text-2xl font-light group-hover:rotate-90 transition-transform duration-500 pointer-events-none">&times;</span>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto no-scrollbar p-8 space-y-10 bg-white">
            <section className="space-y-4">
               <h3 className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  Sélection du compte
               </h3>
               <div className="bg-slate-50 p-6 rounded-[2.5rem] border border-slate-100">
                  <p className="text-[10px] font-black text-text/40 uppercase tracking-widest mb-6 leading-relaxed">
                    Veuillez choisir un utilisateur ayant le rôle d'enseignant dans la liste ci-dessous pour l'associer à votre structure.
                  </p>
                  <CustomAutocomplete
                    options={teachers}
                    value={selectedTeacher}
                    onChange={(value) => setSelectedTeacher(value)}
                    placeholder="Tapez le nom d'un professeur..."
                  />
               </div>
            </section>  
          </div>

          {/* Footer Actions */}
          <div className="p-8 bg-white border-t border-gray-100 flex gap-4 z-40 flex-shrink-0">
             <button 
                onClick={handleCloseAddDialog}
                className="px-8 h-14 rounded-2xl font-black text-[11px] uppercase tracking-widest text-text/40 hover:text-rose-500 hover:bg-rose-50 active:scale-95 transition-all duration-300 pointer-events-auto"
             >
                Annuler
             </button>
             <button 
                onClick={handleSave}
                className="flex-1 h-14 bg-primary text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.3em] shadow-2xl shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-1 active:scale-95 transition-all duration-300 flex items-center justify-center gap-4 group"
             >
                <span>Confirmer l'ajout</span>
                <div className="w-6 h-6 rounded-lg bg-white/20 flex items-center justify-center group-hover:rotate-90 transition-transform duration-500">
                   <FontAwesomeIcon icon={faCheck} className="text-[10px]" />
                </div>
             </button>
          </div>
        </div>
      </Modal>

      <AnimatePresence>
        {openDeleteDialog && (
          <Modal
            open={openDeleteDialog}
            onClose={handleCloseDeleteDialog}
            className="flex items-center justify-center p-4 backdrop-blur-xl bg-title/40"
            closeAfterTransition
          >
            <motion.div 
              className="w-full max-w-[450px] bg-white rounded-[3rem] p-10 shadow-3xl text-center outline-none border border-white/20"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="w-24 h-24 rounded-[2.5rem] bg-rose-50 text-rose-500 flex items-center justify-center text-4xl mx-auto mb-8 border border-rose-100 shadow-sm relative">
                <div className="absolute inset-0 bg-rose-400/20 rounded-[2.5rem] animate-ping opacity-50"></div>
                <FontAwesomeIcon icon={faTrashCan} className="relative z-10" />
              </div>
              
              <h3 className="text-2xl font-black text-title uppercase tracking-tight mb-4">
                Retirer le <span className="text-rose-500">Professeur</span>
              </h3>
              
              <p className="text-sm font-medium text-text/60 leading-relaxed mb-10 px-4">
                Êtes-vous certain de vouloir retirer <span className="text-[#05322B] font-black">{selectedRow?.fullName}</span> de votre équipe ? cette action est irréversible.
              </p>

              <div className="flex gap-4">
                <button
                  className="flex-1 h-16 bg-slate-50 text-text/40 font-black text-[11px] uppercase tracking-widest rounded-2xl hover:bg-slate-100 transition-all duration-300 outline-none"
                  onClick={handleCloseDeleteDialog}
                >
                  Annuler
                </button>
                <button
                  className="flex-1 h-16 bg-rose-500 text-white font-black text-[11px] uppercase tracking-widest rounded-2xl shadow-xl shadow-rose-500/20 hover:bg-rose-600 hover:-translate-y-1 transition-all duration-300 outline-none"
                  onClick={handleDeleteConfirm}
                >
                  Supprimer
                </button>
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ManagementProf;
